import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher, useSearchParams } from 'react-router';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import { Button } from '~/components/Button';
import TextInput from '~/components/Form/TextInput';
import CompleteRegistrationEvent from '~/events/CompleteRegistrationEvent';
import useQsParams from '~/hooks/useQsParams';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';
import { clearPendingRegistrationEmail } from '~/utils/browser';
import { getPageViewId } from '~/utils/navigationContext';

import Modals from '.';
import AuthDialog, {
  authButtonClassName,
  authInputClassName
} from './Login/AuthDialog';
import AuthPanel from './Login/AuthPanel';
import ModalNav from './ModalNav';

export type PostSignUpOptions = {
  action: string;
  legacyProductId: string;
};

const ErrorMessage = ({
  isActive,
  text
}: {
  isActive: boolean;
  text: string;
}) => {
  return isActive ? (
    <div className="flex h-5 flex-row content-center items-center gap-1">
      <ErrorIcon />
      <p className="text-xs font-bold text-red-600">{text}</p>
    </div>
  ) : (
    <></>
  );
};

export const EmailConfirmation = () => {
  const user = useUser();
  const { isBot, clientIp } = useRootData();
  const fetcher = useFetcher<FetcherData>();
  const qsParams = useQsParams();
  const { t } = useTranslation();

  const [, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(
    Boolean(!user.isLoggedIn && qsParams.fromRegistration)
  );
  const [showAppDownload, setShowAppDownload] = useState(false);

  const errors = fetcher.data?.errors;
  const success = fetcher.data?.success;
  const idle = fetcher.state === 'idle';

  const isConfirmationComplete = success && idle;

  const hasFiredNotifyMeSet = useRef(false);

  useEffect(() => {
    if (!isConfirmationComplete) return;

    clearPendingRegistrationEmail();

    setShowModal(false);

    if (
      fetcher.data?.completedAction === 'restock' &&
      qsParams.fromRegistration
    ) {
      setShowAppDownload(true);
    }

    if (
      fetcher.data?.notifiedProducts?.length &&
      !hasFiredNotifyMeSet.current
    ) {
      hasFiredNotifyMeSet.current = true;

      fetcher.data.notifiedProducts.forEach(({ productId, productName }) =>
        gtm.notifyMeSet({
          pagePosition: 'Email Confirmation',
          productId,
          productName
        })
      );
    }

    if (user.isLoggedIn) {
      new CompleteRegistrationEvent({ clientIp, isBot, user }).send();
    }
  }, [
    isConfirmationComplete,
    user,
    clientIp,
    fetcher.data?.completedAction,
    fetcher.data?.notifiedProducts,
    qsParams.fromRegistration,
    isBot
  ]);

  function onDismiss() {
    setSearchParams(
      (params) => {
        params.delete('fromRegistration');
        params.delete('token');
        params.delete('action');
        params.delete('guest_id');
        params.delete('lid');
        params.delete('from_nudge');
        params.delete('client');
        params.delete('medium');
        params.delete('source');

        return params;
      },
      { replace: true }
    );

    setShowModal(false);
    setShowAppDownload(false);
  }

  if (showAppDownload) {
    return <Modals.AppDownload isOpen={true} onDismiss={onDismiss} />;
  }

  return showModal ? (
    <AuthDialog onDismiss={onDismiss}>
      <ModalNav heading={t('login.stagePasswordConfirm.title')} />

      <fetcher.Form action="/bolt/confirm/" className="w-full" method="post">
        <input type="hidden" value={qsParams.action ?? ''} name="action" />
        <input type="hidden" value={qsParams.lid ?? ''} name="lid" />
        <input
          type="hidden"
          value={qsParams.returnUrl ?? ''}
          name="returnUrl"
        />
        <input type="hidden" value={qsParams.guest_id ?? ''} name="guestId" />
        <input type="hidden" value={qsParams.token ?? ''} name="token" />
        <input
          type="hidden"
          value={qsParams.from_nudge ?? ''}
          name="fromNudge"
        />
        <input type="hidden" value={qsParams.client ?? ''} name="client" />
        <input type="hidden" value={qsParams.medium ?? ''} name="medium" />
        <input type="hidden" value={qsParams.source ?? ''} name="source" />
        <input type="hidden" value={getPageViewId() ?? ''} name="viewId" />
        <AuthPanel>
          <TextInput
            backgroundTheme="light"
            className={authInputClassName}
            error={
              errors?.password
                ? t('login.stagePasswordConfirm.passwordFormatError')
                : undefined
            }
            id="confirm-password"
            name="password"
            placeholder={t('login.password')}
            type="password"
          />
          <TextInput
            backgroundTheme="light"
            className={authInputClassName}
            error={
              errors?.mismatch
                ? t('login.stagePasswordConfirm.passwordMismatchError')
                : undefined
            }
            id="confirm-confirm"
            name="confirmedPassword"
            placeholder={t('login.confirmPassword')}
            type="password"
          />
          <Button
            className={authButtonClassName}
            variant={idle ? 'forest' : 'disabled'}
            disabled={!idle}
          >
            {idle ? t('login.signInButton') : t('login.confirming')}
          </Button>
          <ErrorMessage
            isActive={Boolean(errors?.conflict)}
            text={t(
              'login.stagePasswordConfirm.alreadyConfirmedRegistrationError'
            )}
          />
          <ErrorMessage
            isActive={Boolean(errors?.authentication)}
            text={t('login.stagePasswordConfirm.confirmingPasswordError')}
          />
        </AuthPanel>
      </fetcher.Form>
    </AuthDialog>
  ) : null;
};
